
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase, UserProfile } from '../lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

interface AuthState {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
}

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Función fuera del componente para consultar el perfil
async function fetchUserProfile(userId: string, userEmail?: string): Promise<UserProfile | null> {
  console.log('[AUTH] fetchUserProfile START — userId:', userId, 'email:', userEmail);

  try {
    // Query normal sin abortSignal — con Promise.race para timeout
    const queryPromise = supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('TIMEOUT_8S')), 8000)
    );

    const { data, error } = await Promise.race([queryPromise, timeoutPromise]);

    console.log('[AUTH] fetchUserProfile RESPUESTA — data:', !!data, '| error:', error);

    if (error) {
      console.error('[AUTH] Error en query user_profiles:', error);
      return null;
    }

    if (data) {
      console.log('[AUTH] Perfil OK:', data.name, '— role:', data.role, '— status:', data.status);
      // Actualizar last_login en background
      supabase
        .from('user_profiles')
        .update({ last_login: new Date().toISOString() })
        .eq('id', userId)
        .then(() => {});
    } else {
      console.warn('[AUTH] No se encontró perfil para userId:', userId);
    }

    return data;
  } catch (err: any) {
    if (err?.message === 'TIMEOUT_8S') {
      console.error('[AUTH] ⚠️ TIMEOUT — user_profiles no respondió en 8s');
    } else {
      console.error('[AUTH] Error inesperado en fetchUserProfile:', err);
    }
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    profile: null,
    loading: true,
  });

  const handleSession = useCallback(async (session: Session | null, source: string) => {
    console.log(`[AUTH] handleSession (${source}) — hasSession:`, !!session);

    if (!session?.user) {
      setAuthState({ user: null, profile: null, loading: false });
      return;
    }

    const profile = await fetchUserProfile(session.user.id, session.user.email);
    if (profile) {
      setAuthState({ user: session.user, profile, loading: false });
    } else {
      setAuthState({ user: null, profile: null, loading: false });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Safety timeout 12s
    const safetyTimeout = setTimeout(() => {
      if (!cancelled) {
        setAuthState(prev => {
          if (prev.loading) {
            console.error('[AUTH] ⚠️ SAFETY TIMEOUT 12s — forzando loading=false');
            return { user: null, profile: null, loading: false };
          }
          return prev;
        });
      }
    }, 12000);

    const initAuth = async () => {
      console.log('[AUTH] initAuth START');
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        console.log('[AUTH] getSession —', { hasSession: !!session, error });
        if (cancelled) return;
        await handleSession(session, 'initAuth');
      } catch (err) {
        console.error('[AUTH] Error en initAuth:', err);
        if (!cancelled) {
          setAuthState({ user: null, profile: null, loading: false });
        }
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (cancelled) return;
        if (event === 'INITIAL_SESSION') return;
        console.log('[AUTH] onAuthStateChange —', event);
        // No cargar perfil aquí para SIGNED_IN — lo hace signIn directamente
        if (event === 'SIGNED_OUT') {
          setAuthState({ user: null, profile: null, loading: false });
        }
        // SIGNED_IN y TOKEN_REFRESHED se manejan por signIn() o initAuth()
      }
    );

    return () => {
      cancelled = true;
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, [handleSession]);

  const signIn = useCallback(async (email: string, password: string) => {
    console.log('[AUTH] signIn START —', email);
    setAuthState(prev => ({ ...prev, loading: true }));
    
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setAuthState(prev => ({ ...prev, loading: false }));
      throw error;
    }

    console.log('[AUTH] signIn exitoso, cargando perfil...');

    // Cargar perfil directamente — no dependemos del listener
    if (data.session?.user) {
      const profile = await fetchUserProfile(data.session.user.id, data.session.user.email);
      if (profile) {
        console.log('[AUTH] signIn completo — perfil cargado');
        setAuthState({ user: data.session.user, profile, loading: false });
      } else {
        console.error('[AUTH] signIn — no se pudo cargar el perfil');
        setAuthState({ user: null, profile: null, loading: false });
      }
    }

    return data;
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setAuthState({ user: null, profile: null, loading: false });
  }, []);

  return (
    <AuthContext.Provider value={{ ...authState, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext debe usarse dentro de AuthProvider');
  return ctx;
}
